// src/pages/Home/Home.js
import React, { useState, useMemo, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import "./Home.css";
// Map & clustering imports
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import airplaneImage from "../../assets/airplane.png";
import logo from "../../assets/logo.png";
import { useTableList, getTablesByCategory, getAllCategories } from "../../context/TableListContext";

import {
  FaTimes,
  FaBars,
  FaTable,
  FaGlobe,
  FaMobileAlt,
  FaSignOutAlt,
  FaQuestionCircle,
  FaUserCircle,
  FaChevronDown,
  FaChevronRight,
  FaChartPie,
  FaRocket,
  FaMapMarkerAlt,
  FaGlobeAmericas,
  FaBuilding,
  FaStar,
  FaHistory,
  FaSyncAlt,
  FaArrowRight,
  FaClipboardList,
  FaUsers,
  FaCreditCard,
} from "react-icons/fa";

// Whole-world view (Antarctica trimmed so the other continents fill the frame)
const WORLD_BOUNDS = [[-58, -170], [78, 180]];

// Fits the world into the map box, and again whenever the box is resized
function FitWorld() {
  const map = useMap();
  useEffect(() => {
    const fit = () => {
      map.invalidateSize();
      map.fitBounds(WORLD_BOUNDS, { animate: false });
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [map]);
  return null;
}

const VALID_COORDS = /^\s*-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?\s*$/;

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

function Home() {
  const [showHelp, setShowHelp] = useState(false);
  const toggleHelp = () => {
    setShowHelp((prev) => !prev);
  };

  const { currentUser, logout } = useAuth();
  const { tableEndpoints, loading: tableListLoading } = useTableList();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  
  // Dropdown states
  const [showAppManagementDropdown, setShowAppManagementDropdown] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  
  // Get categories and tables
  const categories = getAllCategories();
  const categoryLabels = {
    data_management: 'Data',
    portal_management: 'Portal', 
    sales_management: 'Sales',
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const openInNewTab = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleTableClick = (table) => {
    navigate("/tables", { state: { selectedTable: table } });
  };

  const toggleCategory = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  // Fix default icon paths (CRA / bundler asset handling)
  useMemo(() => {
    delete L.Icon.Default.prototype._getIconUrl; // ensure we override
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
      iconUrl: require('leaflet/dist/images/marker-icon.png'),
      shadowUrl: require('leaflet/dist/images/marker-shadow.png')
    });
  }, []);

  // Lounges state
  const [lounges, setLounges] = useState([]);
  const [loungesLoading, setLoungesLoading] = useState(true);
  const [loungesError, setLoungesError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const fetchLounges = async () => {
      setLoungesLoading(true);
      setLoungesError(null);
      try {
        // console.log(currentUser);
        if (!currentUser || !currentUser.firebaseUser || !currentUser.firebaseUser.getIdToken) {
          setLoungesError("User not authenticated");
          setLoungesLoading(false);
          return;
        }
        const token = await currentUser.firebaseUser.getIdToken();
        // console.log('[LoungeMap] Fetching lounges from /lounges/ with token...');
        const res = await fetch("https://vm.loungeatlas.com/lounges/", {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
        });
        // console.log('[LoungeMap] Response status:', res.status);
        if (!res.ok) {
          const text = await res.text();
          // console.error('[LoungeMap] Error response:', text);
          throw new Error("Failed to fetch lounges");
        }
        const data = await res.json();
        // console.log('[LoungeMap] Lounges data:', data);
        setLounges(data);
      } catch (err) {
        // console.error('[LoungeMap] Fetch error:', err);
        setLoungesError(err.message || "Error fetching lounges");
      } finally {
        setLoungesLoading(false);
      }
    };
    fetchLounges();
  }, [currentUser, reloadKey]);

  // Lounges that can be drawn on the map, and the numbers shown in the summary cards
  const mappableLounges = useMemo(
    () => lounges.filter((l) => l.Coordinates && VALID_COORDS.test(l.Coordinates)),
    [lounges]
  );
  const stats = useMemo(() => {
    const unique = (key) =>
      new Set(lounges.map((l) => (l[key] || "").toString().trim().toLowerCase()).filter(Boolean)).size;
    return {
      total: lounges.length,
      countries: unique("Country"),
      cities: unique("City"),
      prioritized: lounges.filter((l) => l.DisplayPriority !== null && l.DisplayPriority !== undefined).length,
    };
  }, [lounges]);
  // Quick-access tiles for the most-used tables (shown only if this user's role can open them)
  const TABLE_SHORTCUTS = [
    { tag: "Orders", label: "Orders", icon: <FaClipboardList /> },
    { tag: "Lounge", label: "Lounges", icon: <FaMapMarkerAlt /> },
    { tag: "Profiles", label: "Profiles", icon: <FaUsers /> },
    { tag: "Transaction", label: "Transactions", icon: <FaCreditCard /> },
  ];
  const findTable = (tag) => (tableEndpoints || []).find((t) => t.tag === tag);
  const openTable = (tag) => {
    const t = findTable(tag);
    if (t) navigate("/tables", { state: { selectedTable: t } });
  };

  const displayName = currentUser?.first_name || "there";
  const initials =
    ((currentUser?.first_name?.[0] || "") + (currentUser?.last_name?.[0] || "")).toUpperCase() ||
    (currentUser?.email?.[0] || "U").toUpperCase();
  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
  const statValue = (n) => (loungesLoading || loungesError ? "–" : n);

  return (
    <div className="layout-wrapper">
        {/* Top Header */}
        <header className="top-bar" style={{ position: "relative" }}>
          <div className="sidebar-top-row" style={{ position: "absolute", left: 20, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center" }}>
            {!collapsed && (
              <img src={logo} alt="Logo" className="sidebar-logo" />
            )}
            <button
              className="menu-btn sidebar-menu-btn"
              onClick={() => setCollapsed((prev) => !prev)}
              aria-label="Toggle sidebar"
              style={{ marginLeft: !collapsed ? 0 : 0 }}
            >
              <FaBars size={24} />
            </button>
          </div>
          <h1 className="portal-title" style={{ margin: 0, position: "absolute", left: 0, right: 0, textAlign: "center", pointerEvents: "none" }}>Lounge Atlas Portal</h1>
          <div style={{ position: "absolute", right: 20, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 12 }}>
            <button className="class1-btn" onClick={() => navigate("/profile")}
              
            >
              <FaUserCircle size={24}/>
            </button>
            <button className="class1-btn" onClick={toggleHelp}>
              <FaQuestionCircle size={20} />
            </button>
          </div>
        </header>

        {/* Body */}
        <div className="body-section">
          {/* Sidebar */}
          <aside className={`side-panel ${collapsed ? "collapsed" : ""}`} style={{ margin: "0px 0px 0px 0px" }}>
            {/* Airplane image (below logo/menu row) */}
            {!collapsed && (
              <div className="sidebar-image">
                <img src={airplaneImage} alt="Airplane" />
              </div>
            )}

            <nav className="side-menu">
              {/* Quick Start link */}
              <button
                onClick={() => navigate("/quick-start")}
                style={{ border: "1px solid white", borderRadius: "4px", padding: "8px 12px" }}
              >
                <FaRocket />
                {!collapsed && <span>Quick Start</span>}
              </button>
              {/* App Management Dropdown */}
              <div className="dropdown-container">
                <button 
                  className="dropdown-trigger"
                  onClick={() => setShowAppManagementDropdown(!showAppManagementDropdown)}
                >
                  <FaTable />
                  {!collapsed && <span>App Management</span>}
                  {!collapsed && (
                    <FaChevronDown 
                      className={`dropdown-arrow ${showAppManagementDropdown ? 'rotated' : ''}`} 
                    />
                  )}
                </button>
                
                {showAppManagementDropdown && !collapsed && (
                  <div className="dropdown-menu">
                    {categories.map((category) => (
                      <div key={category} className="category-item">
                        <button 
                          className="category-header"
                          onClick={() => toggleCategory(category)}
                        >
                          <FaChevronRight 
                            className={`category-arrow ${expandedCategory === category ? 'rotated' : ''}`} 
                          />
                          <span>{categoryLabels[category] || category}</span>
                        </button>
                        
                        {expandedCategory === category && (
                          <div className="table-list">
                            {getTablesByCategory(category).map((table) => (
                              <button
                                key={table.path}
                                className="table-item"
                                onClick={() => handleTableClick(table)}
                              >
                                <span>{table.tag}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Insights Page */}
              <button onClick={() => navigate("/insights")}> 
                <FaChartPie />
                {!collapsed && <span>Insights</span>}
              </button>

              {/* Activity Log Option */}
              <button onClick={() => navigate("/activity-log")}> 
                <FaUserCircle />
                {!collapsed && <span>Activity Log</span>}
              </button>

              <button onClick={() => openInNewTab("https://ieg-america.com/")}> 
                <FaGlobe />
                {!collapsed && <span>IEG Website</span>}
              </button>
              <button onClick={() => openInNewTab("https://app.loungeatlas.com/")}>
                <FaMobileAlt />
                {!collapsed && <span>Lounge Atlas App</span>}
              </button>
              <button onClick={handleLogout}>
                <FaSignOutAlt />
                {!collapsed && <span>Sign Out</span>}
              </button>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="main-panel home-main">
            {/* Welcome banner */}
            <section className="home-hero">
              <div className="home-hero-text">
                <span className="home-hero-date">{todayLabel}</span>
                <h2>{getGreeting()}, {displayName}</h2>
                <p>Here is a quick look at your Lounge Atlas network.</p>
              </div>
              <div className="home-hero-actions">
                <button className="home-btn home-btn-light" onClick={() => navigate("/quick-start")}>
                  <FaRocket /> Quick Start
                </button>
                <button className="home-btn home-btn-ghost" onClick={() => navigate("/insights")}>
                  <FaChartPie /> Insights
                </button>
              </div>
            </section>

            {/* Summary cards */}
            <section className="home-stats" aria-label="Network summary">
              <div className="home-stat">
                <span className="home-stat-icon"><FaMapMarkerAlt /></span>
                <div>
                  <div className="home-stat-value">{statValue(stats.total)}</div>
                  <div className="home-stat-label">Total lounges</div>
                </div>
              </div>
              <div className="home-stat">
                <span className="home-stat-icon"><FaGlobeAmericas /></span>
                <div>
                  <div className="home-stat-value">{statValue(stats.countries)}</div>
                  <div className="home-stat-label">Countries</div>
                </div>
              </div>
              <div className="home-stat">
                <span className="home-stat-icon"><FaBuilding /></span>
                <div>
                  <div className="home-stat-value">{statValue(stats.cities)}</div>
                  <div className="home-stat-label">Cities</div>
                </div>
              </div>
              <div className="home-stat">
                <span className="home-stat-icon"><FaStar /></span>
                <div>
                  <div className="home-stat-value">{statValue(stats.prioritized)}</div>
                  <div className="home-stat-label">Priority lounges</div>
                </div>
              </div>
            </section>

            <div className="home-grid">
              {/* Left column: account + shortcuts */}
              <div className="home-side">
                <section className="home-card">
                  <h3 className="home-card-title">Your account</h3>
                  <div className="home-account">
                    <span className="home-avatar" aria-hidden="true">{initials}</span>
                    <div className="home-account-name">
                      {[currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(" ") || "User"}
                      {currentUser?.role && <span className="home-chip">{currentUser.role}</span>}
                    </div>
                  </div>
                  <dl className="home-details">
                    <div>
                      <dt>User ID</dt>
                      <dd>{currentUser?.email || "N/A"}</dd>
                    </div>
                    <div>
                      <dt>Organization</dt>
                      <dd>{currentUser?.organization || "N/A"}</dd>
                    </div>
                    <div>
                      <dt>Role</dt>
                      <dd>{currentUser?.role || "N/A"}</dd>
                    </div>
                  </dl>
                </section>

                <section className="home-card">
                                    <h3 className="home-card-title">Quick access</h3>
                  <div className="home-tiles">
                    {TABLE_SHORTCUTS
                      .filter((sc) => tableListLoading || findTable(sc.tag))
                      .map((sc) => (
                        <button
                          key={sc.tag}
                          className="home-tile"
                          disabled={tableListLoading}
                          onClick={() => openTable(sc.tag)}
                        >
                          <span className="home-tile-icon">{sc.icon}</span>
                          <span className="home-tile-label">{sc.label}</span>
                        </button>
                      ))}
                    <button className="home-tile home-tile-wide" onClick={() => navigate("/insights")}>
                      <span className="home-tile-icon"><FaChartPie /></span>
                      <span className="home-tile-label">Insights</span>
                    </button>
                  </div>

                  <div className="home-more">
                    <span className="home-more-title">More</span>
                    <div className="home-links">
                      <button className="home-link" onClick={() => navigate("/activity-log")}>
                        <span className="home-link-icon"><FaHistory /></span>
                        <span>Activity Log</span>
                        <FaArrowRight className="home-link-arrow" />
                      </button>
                      <button className="home-link" onClick={() => openInNewTab("https://app.loungeatlas.com/")}>
                        <span className="home-link-icon"><FaMobileAlt /></span>
                        <span>Lounge Atlas App</span>
                        <FaArrowRight className="home-link-arrow" />
                      </button>
                    </div>
                  </div>
                </section>
              </div>

              {/* Right column: map */}
              <section className="home-card home-map-card">
                <div className="home-map-header">
                  <div>
                    <h3 className="home-card-title">Lounge Atlas Map</h3>
                    <p className="home-map-sub">
                      {loungesLoading || loungesError
                        ? "Where your lounges are located"
                        : `${mappableLounges.length} of ${lounges.length} lounges shown`}
                    </p>
                  </div>
                  <button
                    className="home-icon-btn"
                    onClick={() => setReloadKey((k) => k + 1)}
                    disabled={loungesLoading}
                    title="Refresh lounges"
                    aria-label="Refresh lounges"
                  >
                    <FaSyncAlt className={loungesLoading ? "home-spin" : ""} />
                  </button>
                </div>

                <div className="home-map-frame">
                  {loungesLoading ? (
                    <div className="home-map-state">
                      <span className="home-loader" aria-hidden="true" />
                      <p>Loading lounges…</p>
                    </div>
                  ) : loungesError ? (
                    <div className="home-map-state home-map-state-error" role="alert">
                      <p><strong>We couldn't load the lounges.</strong></p>
                      <p>{loungesError}</p>
                      <button className="home-btn home-btn-primary" onClick={() => setReloadKey((k) => k + 1)}>
                        <FaSyncAlt /> Try again
                      </button>
                    </div>
                  ) : (
                    <MapContainer center={[20, 10]} zoom={2} minZoom={2} worldCopyJump style={{ height: "100%", width: "100%" }}>
                      <FitWorld />
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution="&copy; OpenStreetMap contributors"
                      />
                      <MarkerClusterGroup chunkedLoading showCoverageOnHover={false} maxClusterRadius={50}>
                        {mappableLounges.map((l) => {
                          const [lat, lng] = l.Coordinates.split(",").map(Number);
                          return (
                            <Marker key={l.LoungeID} position={[lat, lng]}>
                              <Popup>
                                <strong>{l.LoungeName}</strong><br />
                                {[l.City, l.Country].filter(Boolean).join(", ")}<br />
                                Lat: {lat.toFixed(4)}, Lng: {lng.toFixed(4)}
                              </Popup>
                            </Marker>
                          );
                        })}
                      </MarkerClusterGroup>
                    </MapContainer>
                  )}
                </div>

                {!loungesLoading && !loungesError && lounges.length > mappableLounges.length && (
                  <p className="home-map-note">
                    {lounges.length - mappableLounges.length} lounge(s) have no valid coordinates and are not on the map.
                  </p>
                )}
              </section>
            </div>
          </main>
        </div>

        {showHelp && (
          <>
            <div className="overlay-backdrop" onClick={toggleHelp}></div>
            <div className="help-overlay">
              <h2>Need Help?</h2>
              <p><strong>Phone:</strong> +1 (514) 871-4910</p>
              <p><strong>Email:</strong> support@loungeatlas.com</p>
              <div style={{marginTop: '1.5rem', textAlign: 'left'}}>
                <h3 style={{fontSize: '1.1rem', color: '#0e265a', marginBottom: 6}}>About Us</h3>
                <p style={{margin: 0, fontSize: '1rem', color: '#333'}}>
                  Lounge Atlas is a product of IEG Business Solutions, a company with nearly 30 years of experience building innovative, in-house software for the airline and airport industry. Our mission is to deliver excellence in business technology through close partnership with our clients and end-users.
                </p>
                <button
                  style={{marginTop: 10, background: '#1572dd', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 18px', fontSize: '1rem', cursor: 'pointer'}}
                  onClick={() => window.open('https://ieg-america.com/about', '_blank', 'noopener,noreferrer')}
                >
                  More
                </button>
              </div>
              <button className="close-icon" onClick={toggleHelp}>
                <FaTimes size={20} />
              </button>
            </div>
          </>
        )}
      </div>
  );
}

export default Home;